'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { formatDayMonthYear } from '@/lib/formatDate';
import { useState } from 'react';
import { useTranslation } from "@/hooks/useTranslation";
const PER_PAGE = 12;

/* coverMode controls the thumbnail box:
     'fixed'   — 190px tall, objectFit:cover, colored gradient fallback when
                 the image is missing. Best for landscape thumbnails (reports,
                 case studies, webinars).
     'natural' — image renders at its natural aspect ratio, capped at 500px
                 tall. No gradient. Best for portrait artwork (ebook covers). */
export default function ResourceGrid({
  items,
  folder,
  color = 'var(--brand)',
  cta,
  label,
  coverMode = 'fixed'
}) {
  const {
    t
  } = useTranslation();
  const [page, setPage] = useState(1);
  const total = Math.ceil(items.length / PER_PAGE);
  const shown = items.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const isNaturalCover = coverMode === 'natural';
  return <section style={{
    background: 'var(--surface-section-soft)',
    padding: '56px 0 64px'
  }}>
      <div className="container">

        {items.length === 0 ? <p style={{
        textAlign: 'center',
        color: '#94A3B8',
        fontFamily: 'inherit',
        padding: '80px 0'
      }}>
            {t("res.content_coming_soon")}
          </p> : <>
            <p style={{
          fontSize: 13,
          color: '#9CA3AF',
          marginBottom: 24,
          fontFamily: 'inherit'
        }}>
              {items.length} {label}{items.length !== 1 ? 's' : ''}
            </p>

            <div className="lb-grid-3 resource-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,1fr)',
          gap: 24
        }}>
              {shown.map((item, i) => <Link key={item.id || i} href={`/resources/${folder}/${item.slug}/`} style={{
            textDecoration: 'none'
          }}>
                  <div style={{
              background: 'var(--surface-card)',
              borderRadius: 'var(--radius-xl)',
              overflow: 'hidden',
              border: '1px solid var(--border, #E8ECF4)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s'
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-card-hover)';
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}>

                    {/* Cover */}
                    <div className={`resource-cover ${isNaturalCover ? 'resource-cover--natural' : 'resource-cover--fixed'}`} style={isNaturalCover ? {
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative',
                maxHeight: 500,
                background: '#F1F4FA'
              } : {
                height: 190,
                overflow: 'hidden',
                background: 'linear-gradient(135deg,var(--brand),var(--brand-dark))',
                flexShrink: 0,
                position: 'relative'
              }}>
                      {(item.featured_image || item.og_image) && <img src={item.featured_image || item.og_image} alt={item.title || ''} loading="lazy" decoding="async" style={isNaturalCover ? {
                  width: '100%',
                  height: 'auto',
                  maxHeight: 500,
                  objectFit: 'cover',
                  display: 'block'
                } : {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }} onError={e => {
                  e.target.style.display = 'none';
                }} />}
                      {item.date && <div style={{
                  position: 'absolute',
                  bottom: 10,
                  left: 12,
                  background: 'rgba(0,0,0,0.5)',
                  backdropFilter: 'blur(4px)',
                  borderRadius: 6,
                  padding: '3px 10px',
                  fontSize: 11,
                  color: '#fff',
                  fontFamily: 'inherit'
                }}>
                          {formatDayMonthYear(item.date)}
                        </div>}
                    </div>

                    {/* Body */}
                    <div style={{
                padding: '18px 20px 22px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                      <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                  fontFamily: 'inherit'
                }}>
                        {item.resource_type || label}
                      </div>
                      <h3 style={{
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  fontSize: 15,
                  color: '#0D1117',
                  marginBottom: 8,
                  lineHeight: 1.45,
                  flex: 1
                }} dangerouslySetInnerHTML={{
                  __html: item.title
                }} />
                      {item.excerpt && <p style={{
                  fontSize: 13,
                  color: '#6B7280',
                  lineHeight: 1.65,
                  marginBottom: 14,
                  fontFamily: 'inherit',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                          {item.excerpt}
                        </p>}
                      {item.company && <p style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color,
                  marginBottom: 8,
                  fontFamily: 'inherit',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                          {item.company}
                        </p>}
                      {item.speaker && <p style={{
                  fontSize: 12,
                  color: '#94A3B8',
                  marginBottom: 8,
                  fontFamily: 'inherit'
                }}>🎤 {item.speaker}</p>}
                      <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color,
                  fontFamily: 'inherit',
                  marginTop: 'auto'
                }}>
                        {cta} →
                      </div>
                    </div>
                  </div>
                </Link>)}
            </div>

            {/* Pagination */}
            {total > 1 && <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          marginTop: 48
        }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{
            padding: '8px 18px',
            borderRadius: 100,
            border: '1px solid #E8ECF4',
            background: '#fff',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            opacity: page === 1 ? 0.4 : 1,
            fontFamily: 'inherit',
            fontSize: 13,
            color: '#374151'
          }}>
                  {t("res.prev")}
                </button>
                {Array.from({
            length: total
          }, (_, i) => i + 1).map(p => <button key={p} onClick={() => setPage(p)} style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '1px solid',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 500,
            borderColor: page === p ? color : '#E8ECF4',
            background: page === p ? color : '#fff',
            color: page === p ? '#fff' : '#374151'
          }}>
                    {p}
                  </button>)}
                <button onClick={() => setPage(p => Math.min(total, p + 1))} disabled={page === total} style={{
            padding: '8px 18px',
            borderRadius: 100,
            border: '1px solid #E8ECF4',
            background: '#fff',
            cursor: page === total ? 'not-allowed' : 'pointer',
            opacity: page === total ? 0.4 : 1,
            fontFamily: 'inherit',
            fontSize: 13,
            color: '#374151'
          }}>
                  {t("res.next")}
                </button>
              </div>}
          </>}
      </div>
      {/* Mobile-only rules. Item stacks 1-per-row, fixed-cover area gets
         a bigger min-height so landscape thumbnails are more present.
         Natural-cover mode (ebooks) is intentionally left alone — those
         size to their own aspect ratio. */}
      <InlineStyle css={`
        @media (max-width: 720px) {
          .resource-grid { grid-template-columns: 1fr !important; gap: 18px !important; }
          .resource-cover--fixed { height: 260px !important; }
        }
      `} />
    </section>;
}